/**
 * Admin Feedback View Page
 * View member feedback for menus grouped by date
 */

import { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useUIStore } from '@/stores/uiStore';
import { WeekSelector } from '@/components/rsvp/WeekSelector';
import { fetchFeedback, exportFeedbackXLSX } from '@/lib/api/feedback';
import {
  getCurrentWeekStart,
  getWeekRange,
  getMonthRange,
  toISODate,
  formatDate,
  fromISODate,
} from '@/lib/utils/dateHelpers';
import { formatRatingSummary } from '@/lib/utils/ratingHelpers';
import { MessageSquare, Star } from 'lucide-react';
import type { FeedbackByDate } from '@/types/api';

type ViewMode = 'week' | 'month';

export function Feedback() {
  const { showToast } = useUIStore();

  const [anonymous, setAnonymous] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Week navigation state
  const [weekStart, setWeekStart] = useState<Date>(() => getCurrentWeekStart());
  const [viewMode, setViewMode] = useState<ViewMode>('week');

  // Data state
  const [feedbackData, setFeedbackData] = useState<FeedbackByDate[]>([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    const { start, end } = viewMode === 'month'
      ? getMonthRange(weekStart)
      : getWeekRange(weekStart);
    return {
      startDate: toISODate(start),
      endDate: toISODate(end),
    };
  }, [weekStart, viewMode]);

  // Ignore responses for ranges the user has already navigated away from.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFeedbackData([]);
    setFeedbackTotal(0);
    fetchFeedback(dateRange).then(response => {
      if (cancelled) return;
      setFeedbackData(response.grouped);
      setFeedbackTotal(response.total);
    }).catch(error => {
      if (!cancelled) {
        showToast(error instanceof Error ? error.message : 'Failed to load feedback', 'error');
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [dateRange, showToast]);

  async function handleExport() {
    setExporting(true);
    try {
      await exportFeedbackXLSX({ ...dateRange, anonymous });
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to export feedback', 'error');
    } finally {
      setExporting(false);
    }
  }

  // Count total feedback items: server-side total for text feedback (the
  // grouped list is page-limited) plus the range-wide rating counts.
  const feedbackCount = useMemo(() => {
    return feedbackTotal + feedbackData.reduce((acc, day) => {
      return acc + day.menus.reduce((menuAcc, menu) => menuAcc + menu.ratingCount, 0);
    }, 0);
  }, [feedbackTotal, feedbackData]);

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Member Feedback</h1>
        <p className="text-muted-foreground">
          View feedback from members on menu items
        </p>
      </div>

      {/* Controls: Week/Month Toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('week')}
          >
            Week
          </Button>
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('month')}
          >
            Month
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={event => setAnonymous(event.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            Anonymous
          </label>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={loading || exporting || feedbackCount === 0}>
            {exporting ? 'Exporting…' : 'Export XLSX'}
          </Button>
          <Badge variant="secondary">
            {feedbackCount} feedback item{feedbackCount !== 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Week/Month Navigation */}
      <WeekSelector
        weekStart={weekStart}
        onWeekChange={setWeekStart}
        disabled={loading}
        navigationMode={viewMode}
      />

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : feedbackData.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No feedback for this {viewMode === 'week' ? 'week' : 'month'}.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Feedback will appear here when members submit comments on menus.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {feedbackData.map((dayData) => (
            <FeedbackDayCard key={dayData.date} data={dayData} anonymous={anonymous} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Internal Components
// ============================================================================

interface FeedbackDayCardProps {
  data: FeedbackByDate;
  anonymous: boolean;
}

/**
 * Card showing all feedback for a single day
 */
function FeedbackDayCard({ data, anonymous }: FeedbackDayCardProps) {
  const dateObj = fromISODate(data.date);
  const formattedDate = formatDate(dateObj, 'EEEE, MMMM d, yyyy');

  // Count total feedback for this day
  const dayFeedbackCount = data.menus.reduce(
    (acc, menu) => acc + menu.feedback.length + menu.ratingCount,
    0
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">
              {data.menus.length === 1 ? data.menus[0].menuTitle : `${data.menus.length} menus`}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{formattedDate}</p>
            {data.menus.length === 1 && data.menus[0].catererName && (
              <p className="text-sm text-muted-foreground">
                Caterer: {data.menus[0].catererName}
              </p>
            )}
          </div>
          <Badge variant="outline">
            {dayFeedbackCount} item{dayFeedbackCount !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {data.menus.map((menu) => (
          <div key={menu.menuId} className="space-y-1">
            {/* Menu Title - only show if multiple menus for this day */}
            {data.menus.length > 1 && (
              <h4 className="font-medium text-sm text-muted-foreground">
                {menu.menuTitle}
                {menu.catererName && (
                  <span className="font-normal"> &middot; Caterer: {menu.catererName}</span>
                )}
              </h4>
            )}

            {/* Star rating summary; the formatted text conveys the content,
                so no ARIA labeling is needed on this container. */}
            <div className="flex items-center gap-1.5 py-2 text-sm">
              <Star
                aria-hidden="true"
                className={
                  menu.ratingCount > 0
                    ? 'h-4 w-4 fill-amber-400 text-amber-500'
                    : 'h-4 w-4 text-muted-foreground'
                }
              />
              <span>{formatRatingSummary(menu.averageRating, menu.ratingCount)}</span>
            </div>
            {menu.feedback.length > 0 && (
              <div className="space-y-1 pl-4 border-l-2 border-muted">
                {menu.feedback.map((item) => (
                  <div key={item.id} className="py-1">
                    <p className="text-sm font-medium text-foreground">
                      {anonymous ? 'Anonymous' : item.memberName}
                    </p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {item.feedbackText}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
